import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import LocalSeoAiBreadcrumbs from "./LocalSeoAiBreadcrumbs"
import LocalSeoAiHeader from "./LocalSeoAiHeader"
import DropdownPopover from 'src/components/UI/DropdownPopover';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import EnableLocalSEOAgentDialog from './EnableLocalSEOAgentDialog';
import EnableAutomationDialog from './EnableAutomationDialog';
import { useRunLocalSEOAgent } from '../hooks/local-seo-apis';
import { useUpdateUserAgents } from '../../hooks/useUpdateUserAgents';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserAgentDetails } from '../../hooks/useGetUserAgentDetails';
import { useGetAgentsStatus } from '../../hooks/useGetAgentsStatus';
import { getProfileDisabledState } from '../../hooks/useIsConfiguredToOtherSite';
import useSiteGBP1to1Mapping, { IGBPLocation } from '../../hooks/useSiteGBP1to1Maping';
import AgentLastRun from '../../Components/AgentLastRun';

const CustomSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'status',
})(() => {
  let activeColor = 'var(--color-secondary)';

  return {
    width: 48,
    height: 26,
    padding: 0,
    display: 'flex',
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'auto',
    },
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 3,
      transitionDuration: '300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
        '& .MuiSwitch-input': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
        }
      },
      '&.Mui-checked': {
        transform: 'translateX(22px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: activeColor,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
    '& .MuiSwitch-track': {
      borderRadius: 13,
      backgroundColor: 'var(--color-bg-mid-grey)',
      opacity: 1,
      transition: 'background-color 300ms',
      '&.Mui-disabled': {
        cursor: 'not-allowed',
        pointerEvents: 'auto',
      },
    },
  };
});

interface LSCommonInterface {
  onSelect: (profile: IGBPLocation) => void;
  selectedProfile: IGBPLocation;
  hasReport?: boolean;
  lastRunTime?: string | number | null;
}

/**
 * `LSCommon` is a layout/container component that renders the shared layout elements 
 * for the Local SEO AI feature interface.
 */
function LSCommon(props: LSCommonInterface) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents, isPending: isUpdatingAgents } = useUpdateUserAgents();

  const { _id: siteId, user_id: userId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId: siteId });

  const { localSEORunMutation, handleLocalSEORun, dialogOpen, setDialogOpen } = useRunLocalSEOAgent();

  const [agentChecked, setAgentChecked] = useState(false);
  const [automationChecked, setAutomationChecked] = useState(false);
  const [openAutomationDialog, setOpenAutomationDialog] = useState(false);

  const { data: userAgentDetails } = useGetUserAgentDetails(siteId);
  const { data: agentsStatus } = useGetAgentsStatus(siteId);
  const localSeoApiData = userAgentDetails?.response?.local_seo;
  const reviewAutomationApiData = userAgentDetails?.response?.review_automation;

  const isOptionDisabled = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).disabled;

  const getOptionDisabledTooltip = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).tooltip;

  // Synchronize agentChecked and automationChecked states with siteGBP1to1Configurations
  useEffect(() => {
    setAgentChecked(siteGBP1to1Configurations.isLocalSEOEnabled.agentEnabled || false);
    setAutomationChecked(siteGBP1to1Configurations.isLocalSEOEnabled.autoApplyEnabled || false);
  }, [siteGBP1to1Configurations.isLocalSEOEnabled]);

  const handleProfileSelect = (location: IGBPLocation) => {
    props.onSelect(location);
  };

  const enableAgent = (profileToUse?: any) => {
    const profile = profileToUse || props.selectedProfile;
    if (!profile) return;

    handleLocalSEORun({
      account_id: String(profile.accountId),
      location_id: profile?.name ? (profile.name.includes('/') ? profile.name.split('/')[1] : profile.name) : String(profile?.locationId),
      local_keywords: ['']
    }, {
      siteId, userId
    } );
  };

  const handleAutoApply = async (autoApply: boolean) => {
    setAutomationChecked(autoApply);
    updateAgents({
      site_id: siteId,
      agents: [
        {
          name: 'local_seo',
          enabled: agentChecked,
          config: {
            account_id: String(props.selectedProfile?.accountId),
            location_id: props.selectedProfile?.name ? (props.selectedProfile.name.includes('/') ? props.selectedProfile.name.split('/')[1] : props.selectedProfile.name) : String(props.selectedProfile?.locationId),
            auto_apply: autoApply,
            local_keywords: ['']
          }
        }
      ]
    }, {
      onSuccess: () => {
        setOpenAutomationDialog(false);
      }
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div>
          <LocalSeoAiBreadcrumbs />
        </div>
        <div className="flex gap-2 items-center">
          {siteGBP1to1Configurations.GBPLocations.length > 0 && (
            <>
              {/* Enable Agent Switch */}
              <FormControlLabel
                control={<CustomSwitch
                  checked={agentChecked}
                  onChange={(_, checked) => {
                    if (checked) {
                      setAgentChecked(true);
                      setDialogOpen(true);
                    } else {
                      setAgentChecked(false);
                      if (siteId) {
                        updateAgents({
                          site_id: siteId,
                          agents: [
                            {
                              name: 'local_seo',
                              enabled: false,
                              config: {
                                account_id: String(props.selectedProfile?.accountId),
                                location_id: props.selectedProfile?.name ? (props.selectedProfile.name.includes('/') ? props.selectedProfile.name.split('/')[1] : props.selectedProfile.name) : String(props.selectedProfile?.locationId),
                                auto_apply: false,
                                local_keywords: ['']
                              }
                            }
                          ]
                        });
                        queryClient.removeQueries({
                          queryKey: ["getLocalSEOReport"]
                        });
                      }
                    }
                  }}
                />}
                label="Enable Agent"
                labelPlacement="start"
                sx={{
                  paddingRight: "10px",
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 600,
                    color: '#2B2F36',
                    fontSize: '14px',
                    marginRight: "10px"
                  },
                  whiteSpace: "nowrap"
                }}
              />

              {/* Enable Automation Switch */}
              <FormControlLabel
                control={<CustomSwitch
                  checked={automationChecked}
                  disabled={!agentChecked}
                  onChange={(_, checked) => {
                    if (checked) {
                      setOpenAutomationDialog(true);
                    } else {
                      handleAutoApply(false);
                    }
                  }}
                />}
                label="Enable Automation"
                labelPlacement="start"
                sx={{
                  paddingRight: "25px",
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 600,
                    color: '#2B2F36',
                    fontSize: '14px',
                    marginRight: "10px"
                  },
                  whiteSpace: "nowrap"
                }}
              />

              {/* Google Business Profile Selector */}
              <div aria-label="google-business-profile" style={{ minWidth: 200 }}>
                <DropdownPopover
                  startIcon="/static/images/icons/local-seo/google-home.svg"
                  selectedValue={props.selectedProfile}
                  options={siteGBP1to1Configurations.GBPLocations || []}
                  onSelect={handleProfileSelect}
                  isArrayOfObject={true}
                  labelKey="title"
                  idKey="name"
                  placeholder="Select Business"
                  enableSearch={siteGBP1to1Configurations.GBPLocations?.length > 5}
                  searchPlaceholder="Search profiles..."
                  isOptionDisabled={isOptionDisabled}
                  getOptionDisabledTooltip={getOptionDisabledTooltip}
                />
              </div>
            </>
          )}
          <div className="flex flex-col items-end">
            <LocalSeoAiHeader />
            <AgentLastRun
              timestamp={props.hasReport ? props.lastRunTime : null}
            />
          </div>
        </div>
      </div>

      <EnableLocalSEOAgentDialog
        GBPLocations={[]}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setAgentChecked(false);
        }}
        onEnable={enableAgent}
        isLoading={localSEORunMutation.isPending}
        showProfileSelector={false}
        selectedProfile={props.selectedProfile}
      />

      <EnableAutomationDialog
        open={openAutomationDialog}
        onClose={() => {
          setOpenAutomationDialog(false);
        }}
        onConfirm={() => handleAutoApply(true)}
        isLoading={isUpdatingAgents}
      />
    </>
  );
}

export default LSCommon;
